import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { FormModal } from '@/components/ui/form-modal';
import { Button } from '@/components/ui/button';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import config from 'constants/apiConfig';
import { changeGroupAdminNameToOrgAdmin } from 'components/Roles/RolesUtils/groupToOrgTransformations';
import type { InternalLayer, LayerGrant } from 'hooks/internalLayer/types';
import { usePublishInternalLayerMutation } from 'hooks/internalLayer/usePublishInternalLayerMutation';

type PublishRoleScope = {
  roleId: string;
  roleName: string;
  scopeType: 'group' | 'aor';
  scopeId: string;
  scopeLabel: string;
};

function optionValue(role: PublishRoleScope) {
  return `${role.roleId}|${role.scopeType}|${role.scopeId}`;
}

function grantsFromSelection(selected: string[]): LayerGrant[] {
  return selected.flatMap((value) => {
    const [subjectId, scopeType, scopeId] = value.split('|');
    if (
      !subjectId ||
      (scopeType !== 'group' && scopeType !== 'aor') ||
      !scopeId
    ) {
      return [];
    }
    return [
      {
        subjectType: 'role' as const,
        subjectId,
        canView: true,
        canEdit: false,
        scopeType,
        scopeId,
      },
    ];
  });
}

export function PublishToOrganizationDialog({
  layer,
  open,
  onClose,
}: {
  layer: InternalLayer;
  open: boolean;
  onClose: () => void;
}) {
  const publishLayer = usePublishInternalLayerMutation();
  const [selected, setSelected] = useState<string[]>([]);
  const { data: roles = [], isFetched } = useQuery({
    queryKey: ['layer-publish-roles', layer.groupGuid],
    enabled: open && Boolean(layer.groupGuid),
    queryFn: async () => {
      const response = await axios.post<PublishRoleScope[]>(
        config.apiGateway.fetchRolesForLayerPublish,
        {
          // eslint-disable-next-line camelcase -- matches the roles API body
          group_guid: layer.groupGuid,
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const options = useMemo(
    () =>
      roles.map((role) => ({
        value: optionValue(role),
        label: `${changeGroupAdminNameToOrgAdmin(role.roleName)} · ${role.scopeLabel}`,
      })),
    [roles]
  );

  const close = () => {
    if (publishLayer.isPending) return;
    setSelected([]);
    onClose();
  };

  const confirm = async () => {
    const grants = grantsFromSelection(selected);
    if (grants.length === 0) return;
    await publishLayer.mutateAsync({ internalLayerId: layer.id, grants });
    setSelected([]);
    onClose();
  };

  return (
    <FormModal
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title="Publish to Organization"
      description="People outside this incident workspace will be able to view this layer. Choose which organization or area of responsibility roles can view it."
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={close}
            disabled={publishLayer.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void confirm()}
            disabled={
              publishLayer.isPending ||
              !isFetched ||
              grantsFromSelection(selected).length === 0
            }
          >
            {publishLayer.isPending ? 'Publishing…' : 'Publish to Organization'}
          </Button>
        </>
      }
    >
      {!isFetched ? (
        <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
          Loading roles…
        </p>
      ) : options.length === 0 ? (
        <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
          The organization has no roles assigned at organization or area of
          responsibility scope.
        </p>
      ) : (
        <label className="tw:flex tw:flex-col tw:gap-2 tw:text-sm">
          <span>Who can view</span>
          <MultiIdCombobox
            options={options}
            value={selected}
            onChange={setSelected}
            placeholder="Search roles"
            disabled={publishLayer.isPending}
          />
        </label>
      )}
    </FormModal>
  );
}
